import { ListingSchemaView } from "shared/app/ListingTemplate/slices/types";
import {
  ListingButtons,
  PrescreeningMode,
  PrescreeningQuestion
} from "shared/types";
import { ApplicationFee } from "types/landlord/verificationTemplate";

export interface TemplateAvailabilityRule {
  id: number;
  subject_type: string;
  subject_id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export interface ReplaceTemplateAvailabilityPayload {
  availabilities: Omit<
    TemplateAvailabilityRule,
    "id" | "subject_type" | "subject_id"
  >[];
}

export interface TemplateTimeWindow {
  start_time: string;
  end_time: string;
}

export interface TemplateDateOverride {
  id: number;
  subject_type: string;
  subject_id: number;
  override_date: string;
  unavailable_all_day: boolean;
  start_time?: string | null;
  end_time?: string | null;
  created_at: string;
}

export interface SetTemplateDateOverridePayload {
  override_date: string;
  unavailable_all_day?: boolean;
  windows?: TemplateTimeWindow[];
}

export interface RemoveTemplateDateOverridePayload {
  override_date: string;
}

export type RentDisplayMode = "both" | "home" | "site";

export enum IdentityVerificationMode {
  Plaid = "plaid",
  Persona = "persona",
  ManualUpload = "manual_upload"
}

type IdentityVerification = {
  mode: IdentityVerificationMode;
  price: ApplicationFee;
};
type ServicesFee = {
  [ListingSchemaView.SelfReportedPrescreening]: ApplicationFee;
  [ListingSchemaView.IdentityVerification]: IdentityVerification[];
  [ListingSchemaView.ShowingScheduling]: ApplicationFee;
};
export type PartnerUser = {
  id: number;
  name: string;
  email: string;
};

export type PrescreeningQuestionPayload = Omit<
  PrescreeningQuestion,
  "id" | "order" | "type"
>;
export type PrescreeningTemplate = ListingButtons & {
  id: number;
  identifier: string;
  name: string;
  version: number;
  default: boolean;
  active: boolean;
  credit_score_enabled: boolean;
  credit_score_input_mode: PrescreeningMode | null;
  income_enabled: boolean;
  income_input_mode: PrescreeningMode | null;
  prescreening_questions: PrescreeningQuestion[];
  identity_verification_enabled: boolean;
  identity_verification_mode: IdentityVerificationMode;
  selfie_check_enabled: boolean;
  allow_schedule_on_idv_fail: boolean;
  require_new_idv: boolean;
  self_reported_prescreening_enabled: boolean;
  showing_scheduling_enabled: boolean;
  showing_type: "guided" | "self_guided" | null;
  custom_question_enabled: boolean;
  rent_display_mode: RentDisplayMode | null;
  entry_instructions: string | null;
  created_at: string;
  updated_at: string;
  last_modified_by: PartnerUser;
  last_modified_at: string;
  created_by: PartnerUser;
  fee: ApplicationFee;
  service_fees: ServicesFee;
  availabilities?: Omit<
    TemplateAvailabilityRule,
    "id" | "subject_type" | "subject_id"
  >[];
  date_overrides?: SetTemplateDateOverridePayload[];
};

export type PrescreeningTemplatePayload = Partial<
  Omit<
    PrescreeningTemplate,
    | "id"
    | "identifier"
    | "version"
    | "default"
    | "active"
    | "created_at"
    | "updated_at"
    | "last_modified_by"
    | "last_modified_at"
    | "created_by"
    | "prescreening_questions"
  >
> & {
  prescreening_questions?: PrescreeningQuestionPayload[];
};

export interface RangesResponse {
  credit_score_ranges: Range[];
  income_ranges: Range[];
}

export interface GetPrescreeningTemplatesResponse {
  items: PrescreeningTemplate[];
}
